import React, { useContext, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./CapabilitiesTab.css";

const TYPE_OPTIONS = ["Fungsional", "Kepemimpinan"];

const LEADERSHIP_LEVEL_LABELS = {
  essential: "Tidak Terampil",
  advance: "Terampil",
  expert: "Berlebihan",
};

const FUNCTIONAL_LEVEL_LABELS = {
  essential: "Essential",
  advance: "Advance",
  expert: "Expert",
};

export default function CapabilitiesTab() {
  const { user } = useContext(AuthContext);
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  // data
  const [abilities, setAbilities] = useState([]);
  const [compsAll, setCompsAll] = useState([]);
  const [loading, setLoading] = useState(true);

  // filter
  const [typeFilter, setTypeFilter] = useState("");
  const [capFilter, setCapFilter] = useState("");

  // add capability
  const [addCapOpen, setAddCapOpen] = useState(false);
  const [capEditId, setCapEditId] = useState(null);
  const [savingCap, setSavingCap] = useState(false);
  const [capForm, setCapForm] = useState({
    type: "Fungsional",
    title: "",
    description: "",
    learn70: "",
    learn20: "",
    learn10: "",
  });

  // add competency (tanpa timeline)
  const [addCompOpen, setAddCompOpen] = useState(false);
  const [savingComp, setSavingComp] = useState(false);
  const [compForm, setCompForm] = useState({
    capability_id: "",
    name: "",
    essential: "",
    advance: "",
    expert: "",
  });

  // detail competency
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    essential: "",
    advance: "",
    expert: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const loadAllData = async () => {
    try {
      setLoading(true);
      const [capRes, compRes] = await Promise.all([
        axios.get(`${apiUrl}/capdev/capability`),
        axios.get(`${apiUrl}/capdev/competencies/all`),
      ]);
      setAbilities(capRes?.data?.data || []);
      setCompsAll(compRes?.data?.data || []);
    } catch (e) {
      console.error("Load capabilities/competencies error:", e);
      setAbilities([]);
      setCompsAll([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  // group competencies by capability_id
  const compsByCap = useMemo(() => {
    const map = {};
    for (const c of compsAll) {
      if (!map[c.capability_id]) map[c.capability_id] = [];
      map[c.capability_id].push(c);
    }
    Object.keys(map).forEach((k) =>
      map[k].sort((a, b) => a.name.localeCompare(b.name))
    );
    return map;
  }, [compsAll]);

  const selectedCompCapability = useMemo(
    () => abilities.find((a) => Number(a.id) === Number(compForm.capability_id)) || null,
    [abilities, compForm.capability_id]
  );

  const compLevelLabels = useMemo(() => {
    const selectedType = String(selectedCompCapability?.type || "").toLowerCase();
    if (selectedType === "kepemimpinan") return LEADERSHIP_LEVEL_LABELS;
    return FUNCTIONAL_LEVEL_LABELS;
  }, [selectedCompCapability]);
  const detailCompCapability = useMemo(
    () => abilities.find((a) => Number(a.id) === Number(detail?.capability_id)) || null,
    [abilities, detail?.capability_id]
  );

  const detailLevelLabels = useMemo(() => {
    const selectedType = String(detailCompCapability?.type || "").toLowerCase();
    if (selectedType === "kepemimpinan") return LEADERSHIP_LEVEL_LABELS;
    return FUNCTIONAL_LEVEL_LABELS;
  }, [detailCompCapability]);

  // filter abilities
  const abilityOptions = useMemo(() => {
    let list = abilities.slice();
    if (typeFilter) list = list.filter((a) => a.type === typeFilter);
    return list;
  }, [abilities, typeFilter]);

  const displayAbilities = useMemo(() => {
    let list = abilityOptions;
    if (capFilter) list = list.filter((a) => a.id === Number(capFilter));
    return list;
  }, [abilityOptions, capFilter]);

  // open detail -> fetch by id (agar indikator selalu ada)
  const openDetail = async (c) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axios.get(`${apiUrl}/capdev/competency/${c.id}`);
      const d = res?.data?.data || c;
      setDetail(d);
      setEditForm({
        name: d.name || "",
        essential: d.essential || "",
        advance: d.advance || "",
        expert: d.expert || "",
      });
    } catch (e) {
      // fallback pakai data list
      setDetail(c);
      setEditForm({
        name: c.name || "",
        essential: c.essential || "",
        advance: c.advance || "",
        expert: c.expert || "",
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const saveEditCompetency = async () => {
    if (!detail?.id || savingEdit) return;
    try {
      setSavingEdit(true);
      const payload = {
        name: editForm.name,
        essential: editForm.essential,
        advance: editForm.advance,
        expert: editForm.expert,
        user_role: user?.role,
      };
      const res = await axios.put(
        `${apiUrl}/capdev/competency/${detail.id}`,
        payload
      );
      if (res?.data?.success) {
        // update cache
        setCompsAll((prev) =>
          prev.map((x) =>
            x.id === detail.id ? { ...x, ...payload } : x
          )
        );
        setDetail((d) => d && { ...d, ...payload });
        setEditing(false);
        alert("Kompetensi diperbarui.");
      } else {
        alert(res?.data?.message || "Gagal memperbarui kompetensi.");
      }
    } catch (e) {
      if (e?.response?.status === 403) alert("Hanya admin yang dapat menyimpan.");
      else alert("Gagal memperbarui kompetensi.");
    } finally {
      setSavingEdit(false);
    }
  };
  // submit add / edit capability
  const submitCap = async () => {
    if (savingCap) return;
    const { type, title, description, learn70, learn20, learn10 } = capForm;
    if (!type || !title || !description || !learn70 || !learn20 || !learn10) {
      alert("Semua field kapabilitas wajib diisi.");
      return;
    }
    try {
      setSavingCap(true);
      const payload = {
        ...capForm,
        created_by: user?.id,
        user_role: user?.role,
      };

      const res = capEditId
        ? await axios.put(`${apiUrl}/capdev/capability/${capEditId}`, payload)
        : await axios.post(`${apiUrl}/capdev/capability`, payload);

      if (res?.data?.success) {
        await loadAllData();
        setAddCapOpen(false);
        setCapEditId(null);
        setCapForm({
          type: "Fungsional",
          title: "",
          description: "",
          learn70: "",
          learn20: "",
          learn10: "",
        });
      } else {
        alert(
          res?.data?.message ||
            (capEditId ? "Gagal memperbarui kapabilitas." : "Gagal menambahkan kapabilitas.")
        );
      }
    } catch (e) {
      if (e?.response?.status === 403) alert("Hanya admin yang dapat mengelola data.");
      else
        alert(capEditId ? "Gagal memperbarui kapabilitas." : "Gagal menambahkan kapabilitas.");
    } finally {
      setSavingCap(false);
    }
  };

  const openAddCapability = () => {
    setCapEditId(null);
    setCapForm({
      type: "Fungsional",
      title: "",
      description: "",
      learn70: "",
      learn20: "",
      learn10: "",
    });
    setAddCapOpen(true);
  };

  const openEditCapability = (cap) => {
    setCapEditId(cap.id);
    setCapForm({
      type: cap.type || "Fungsional",
      title: cap.title || "",
      description: cap.description || "",
      learn70: cap.learn70 || "",
      learn20: cap.learn20 || "",
      learn10: cap.learn10 || "",
    });
    setAddCapOpen(true);
  };

  const deleteCapability = async (cap) => {
    const ok = window.confirm(`Hapus kapabilitas "${cap.title}" beserta kompetensinya?`);
    if (!ok) return;

    try {
      const res = await axios.delete(`${apiUrl}/capdev/capability/${cap.id}`, {
        data: { user_role: user?.role },
      });
      if (res?.data?.success) {
        await loadAllData();
        if (Number(capFilter) === Number(cap.id)) setCapFilter("");
      } else {
        alert(res?.data?.message || "Gagal menghapus kapabilitas.");
      }
    } catch (e) {
      if (e?.response?.status === 403) alert("Hanya admin yang dapat menghapus.");
      else alert(e?.response?.data?.message || "Gagal menghapus kapabilitas.");
    }
  };

  const deleteCompetency = async (comp) => {
    const ok = window.confirm(`Hapus kompetensi "${comp.name}"?`);
    if (!ok) return;

    try {
      const res = await axios.delete(`${apiUrl}/capdev/competency/${comp.id}`, {
        data: { user_role: user?.role },
      });
      if (res?.data?.success) {
        await loadAllData();
        if (detail?.id === comp.id) {
          setDetailOpen(false);
          setDetail(null);
          setEditing(false);
        }
      } else {
        alert(res?.data?.message || "Gagal menghapus kompetensi.");
      }
    } catch (e) {
      if (e?.response?.status === 403) alert("Hanya admin yang dapat menghapus.");
      else alert(e?.response?.data?.message || "Gagal menghapus kompetensi.");
    }
  };

  // submit add competency
  const submitComp = async () => {
    if (savingComp) return;
    const { capability_id, name, essential, advance, expert } = compForm;
    if (!capability_id || !name || !essential || !advance || !expert) {
      alert("Semua field kompetensi wajib diisi.");
      return;
    }
    try {
      setSavingComp(true);
      const res = await axios.post(`${apiUrl}/capdev/competency`, {
        capability_id,
        name,
        essential,
        advance,
        expert,
        user_role: user?.role,
      });
      if (res?.data?.success) {
        await loadAllData();
        setAddCompOpen(false);
        setCompForm({
          capability_id: "",
          name: "",
          essential: "",
          advance: "",
          expert: "",
        });
      } else {
        alert(res?.data?.message || "Gagal menambahkan kompetensi.");
      }
    } catch (e) {
      if (e?.response?.status === 403) alert("Hanya admin yang dapat menambahkan.");
      else alert("Gagal menambahkan kompetensi.");
    } finally {
      setSavingComp(false);
    }
  };

  return (
    <div className="cap-wrap">
      {/* Sticky bar: filters & actions */}
      <div className="cap-sticky">
        <div className="cap-toolbar">
          <div className="left">
            <label className="filter">
              <span>Jenis</span>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCapFilter("");
                }}
              >
                <option value="">Semua Jenis</option>
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>

            <label className="filter">
              <span>Filter Kapabilitas</span>
              <select
                value={capFilter}
                onChange={(e) => setCapFilter(e.target.value)}
              >
                <option value="">Semua Kapabilitas</option>
                {abilityOptions.map((a) => (
                  <option key={a.id} value={a.id}>{a.title}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="right">
            {isAdmin && (
              <>
                <button className="btn" onClick={openAddCapability}>
                  + Tambah Kapabilitas
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    const defCap = displayAbilities[0]?.id || "";
                    setCompForm({
                      capability_id: String(defCap),
                      name: "",
                      essential: "",
                      advance: "",
                      expert: "",
                    });
                    setAddCompOpen(true);
                  }}
                >
                  + Tambah Kompetensi
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="muted">Memuat...</div>
      ) : displayAbilities.length === 0 ? (
        <div className="muted">Belum ada kapabilitas.</div>
      ) : (
        displayAbilities.map((a) => (
          <div key={a.id} className="cap-card">
            <div className="cap-title-row">
              <div className="cap-title">
                <strong>Kapabilitas:</strong> {a.title} - <em>{a.type}</em>
              </div>
              {isAdmin && (
                <div className="cap-actions">
                  <button className="btn-sm btn-edit" onClick={() => openEditCapability(a)}>
                    Edit Capability
                  </button>
                  <button className="btn-sm btn-delete" onClick={() => deleteCapability(a)}>
                    Delete Capability
                  </button>
                </div>
              )}
            </div>

            <div className="cap-two">
              {/* Kiri: deskripsi & 70/20/10 */}
              <div className="cap-main">
                <div className="cap-desc">{a.description}</div>

                <div className="learn-title">Metode Pembelajaran</div>
                <div className="learn-grid">
                  <div className="learn-col">
                    <div className="lg-head">70% - Pembelajaran Berbasis Aplikasi</div>
                    <div className="pre">{a.learn70}</div>
                  </div>
                  <div className="learn-col">
                    <div className="lg-head">20% - Belajar Dari Orang Lain</div>
                    <div className="pre">{a.learn20}</div>
                  </div>
                  <div className="learn-col">
                    <div className="lg-head">10% - Pembelajaran Lain</div>
                    <div className="pre">{a.learn10}</div>
                  </div>
                </div>
              </div>

              {/* Kanan: Kompetensi Pendukung */}
              <aside className="cap-aside">
                <div className="aside-head">Kompetensi Pendukung</div>
                <ol className="aside-list">
                  {(compsByCap[a.id] || []).map((c) => (
                    <li key={c.id} className="comp-item">
                      <button className="link" onClick={() => openDetail(c)}>
                        {c.name}
                      </button>
                      {isAdmin && (
                        <div className="comp-actions">
                          <button className="mini-btn mini-edit" onClick={() => openDetail(c)}>
                            Edit
                          </button>
                          <button className="mini-btn mini-del" onClick={() => deleteCompetency(c)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                  {(compsByCap[a.id] || []).length === 0 && (
                    <div className="muted">Belum ada kompetensi.</div>
                  )}
                </ol>
              </aside>
            </div>
          </div>
        ))
      )}

      {/* Modal Tambah Kapabilitas */}
      {addCapOpen && ReactDOM.createPortal(
        <div className="modal-backdrop" onClick={() => {
          if (savingCap) return;
          setAddCapOpen(false);
          setCapEditId(null);
        }}>
          <div className="modal modal-cap" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h4>{capEditId ? "Edit Kapabilitas" : "Tambah Kapabilitas"}</h4>
              <button className="x" onClick={() => {
                if (savingCap) return;
                setAddCapOpen(false);
                setCapEditId(null);
              }}>x</button>
            </div>
            <div className="form">
              <label>
                <span>Jenis</span>
                <select
                  value={capForm.type}
                  onChange={(e) => setCapForm({ ...capForm, type: e.target.value })}
                >
                  {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>

              <label>
                <span>Kapabilitas (Judul)</span>
                <input
                  type="text"
                  value={capForm.title}
                  onChange={(e) => setCapForm({ ...capForm, title: e.target.value })}
                />
              </label>

              <label>
                <span>Deskripsi</span>
                <textarea
                  value={capForm.description}
                  onChange={(e) => setCapForm({ ...capForm, description: e.target.value })}
                ></textarea>
              </label>

              <label>
                    <div className="lg-head">70% - Pembelajaran Berbasis Aplikasi</div>
                <textarea
                  value={capForm.learn70}
                  onChange={(e) => setCapForm({ ...capForm, learn70: e.target.value })}
                ></textarea>
              </label>

              <label>
                    <div className="lg-head">20% - Belajar Dari Orang Lain</div>
                <textarea
                  value={capForm.learn20}
                  onChange={(e) => setCapForm({ ...capForm, learn20: e.target.value })}
                ></textarea>
              </label>

              <label>
                    <div className="lg-head">10% - Pembelajaran Lain</div>
                <textarea
                  value={capForm.learn10}
                  onChange={(e) => setCapForm({ ...capForm, learn10: e.target.value })}
                ></textarea>
              </label>

              <div className="modal-actions">
                <button className="btn" onClick={submitCap} disabled={savingCap}>
                  {savingCap ? "Menyimpan..." : capEditId ? "Update" : "Tambah"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Tambah Kompetensi */}
      {addCompOpen && ReactDOM.createPortal(
        <div className="modal-backdrop" onClick={() => !savingComp && setAddCompOpen(false)}>
          <div className="modal modal-comp" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h4>Tambah Kompetensi</h4>
              <button className="x" onClick={() => !savingComp && setAddCompOpen(false)}>x</button>
            </div>
            <div className="form">
              <label>
                <span>Kapabilitas</span>
                <select
                  value={compForm.capability_id}
                  onChange={(e) => setCompForm({ ...compForm, capability_id: e.target.value })}
                >
                  <option value="">Pilih Kapabilitas</option>
                  {displayAbilities.map((a) => (
                    <option key={a.id} value={a.id}>{a.title}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Nama Kompetensi</span>
                <input
                  type="text"
                  value={compForm.name}
                  onChange={(e) => setCompForm({ ...compForm, name: e.target.value })}
                />
              </label>

              <label>
                <span>{compLevelLabels.essential}</span>
                <textarea
                  value={compForm.essential}
                  onChange={(e) => setCompForm({ ...compForm, essential: e.target.value })}
                ></textarea>
              </label>

              <label>
                <span>{compLevelLabels.advance}</span>
                <textarea
                  value={compForm.advance}
                  onChange={(e) => setCompForm({ ...compForm, advance: e.target.value })}
                ></textarea>
              </label>

              <label>
                <span>{compLevelLabels.expert}</span>
                <textarea
                  value={compForm.expert}
                  onChange={(e) => setCompForm({ ...compForm, expert: e.target.value })}
                ></textarea>
              </label>

              <div className="modal-actions">
                <button className="btn" onClick={submitComp} disabled={savingComp}>
                  {savingComp ? "Menyimpan..." : "Tambah"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Detail Kompetensi */}
      {detailOpen && ReactDOM.createPortal(
        <div className="modal-backdrop" onClick={() => setDetailOpen(false)}>
          <div className="modal modal-detail" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h4>Detail Kompetensi</h4>
              <button className="x" onClick={() => setDetailOpen(false)}>x</button>
            </div>
            {detailLoading ? (
              <div className="muted">Memuat...</div>
            ) : detail ? (
              <div className="form">
                <label>
                  <span>Nama Kompetensi</span>
                  {editing ? (
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  ) : (
                    <div>{detail.name}</div>
                  )}
                </label>

                <label>
                  <span>{detailLevelLabels.essential}</span>
                  {editing ? (
                    <textarea
                      value={editForm.essential}
                      onChange={(e) => setEditForm({ ...editForm, essential: e.target.value })}
                    ></textarea>
                  ) : (
                    <div className="pre">{detail.essential}</div>
                  )}
                </label>

                <label>
                  <span>{detailLevelLabels.advance}</span>
                  {editing ? (
                    <textarea
                      value={editForm.advance}
                      onChange={(e) => setEditForm({ ...editForm, advance: e.target.value })}
                    ></textarea>
                  ) : (
                    <div className="pre">{detail.advance}</div>
                  )}
                </label>

                <label>
                  <span>{detailLevelLabels.expert}</span>
                  {editing ? (
                    <textarea
                      value={editForm.expert}
                      onChange={(e) => setEditForm({ ...editForm, expert: e.target.value })}
                    ></textarea>
                  ) : (
                    <div className="pre">{detail.expert}</div>
                  )}
                </label>

                <div className="modal-actions">
                  {isAdmin && (
                    editing ? (
                      <>
                        <button className="btn" onClick={saveEditCompetency} disabled={savingEdit}>
                          {savingEdit ? "Menyimpan..." : "Simpan"}
                        </button>
                        <button className="btn" onClick={() => setEditing(false)}>
                          Batal
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn" onClick={() => setEditing(true)}>
                          Edit
                        </button>
                        <button className="btn btn-danger" onClick={() => deleteCompetency(detail)}>
                          Delete
                        </button>
                      </>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="muted">Data tidak ditemukan.</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}



















